import React, { FC, useState } from "react";
import toast from "react-hot-toast";
import DialogLayout from "../DialogLayout";
import Typography from "../../components/Typography/Typography";
import ASSETS from "../../assets";
import Button from "../../components/Button/Button";
import { authAPI, DeleteAzureUserRequest, AzureUser } from "../../services/api";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  user: AzureUser | null;
  onSuccess: () => void;
}

const DeleteAzureUser: FC<IProps> = ({ open, setOpen, user, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      const deleteData: DeleteAzureUserRequest = {
        email: user.displayName,
      };

      const response = await authAPI.deleteAzureUser(deleteData);
      
      // Show success message
      toast.success(response.message || 'Azure user deleted successfully!');
      
      // Close modal
      handleClose();
      
      // Refresh Azure users data
      onSuccess();
      
    } catch (err: any) {
      let errorMessage = 'Failed to delete Azure user. Please try again.';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 404) {
        errorMessage = 'User not found.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid request. Please try again.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <DialogLayout open={open}>
      <div className="w-full max-w-[536px] p-[25px] bg-white rounded-[15px] relative">
        <div
          className="absolute top-[25px] right-[25px] rounded-full size-[30px] bg-[#0000000D] cursor-pointer grid place-items-center"
          onClick={handleClose}
        >
          <img src={ASSETS.cross} alt="" />
        </div>
        <Typography variant="h2" className="font-bold title">
          Delete Azure User
        </Typography>
        <Typography
          variant="body1"
          color="grey"
          className="mt-[5px] font-medium"
        >
          Are you sure you want to delete this Azure user? This action cannot be undone.
        </Typography>
        
        <div className="border border-k-border rounded-lg p-6 mt-[25px] flex flex-col gap-6">
          {user && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <Typography variant="body2" className="text-gray-600 mb-2">
                User Details:
              </Typography>
              <div className="space-y-1">
                <Typography variant="body2" className="text-gray-800">
                  <span className="font-medium">Name:</span> {user.firstName} {user.lastName}
                </Typography>
                <Typography variant="body2" className="text-gray-800">
                  <span className="font-medium">Email:</span> {user.displayName}
                </Typography>
              </div>
            </div>
          )}
          
          <div className="flex gap-3">
            <Button
              label="Cancel"
              variant="outlined"
              className="flex-1 h-[45px]"
              onClick={handleClose}
              disabled={loading}
              type="button"
            />
            <Button
              label={loading ? "Deleting..." : "Delete User"}
              variant="primary"
              className="flex-1 h-[45px] disabled:opacity-200 disabled:cursor-not-allowed disabled:!text-gray-400"
              onClick={handleDelete}
              loading={loading}
              disabled={loading}
              type="button"
            />
          </div>
        </div>
      </div>
    </DialogLayout>
  );
};

export default DeleteAzureUser;
